import React, {useEffect, useState} from 'react'
import {Button, Descriptions, Spin, Tag, message} from 'antd'
import {ArrowLeft, Download, FileArchive, Pencil, Server, Cloud} from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {APIv1} from '../../api'
import {versionStatusTag} from '../../utils/version/version_status_tag'
import {resolveIsEditPermission} from '../../utils/userPermissions'
import {getAuthHeaders, normalVersionConfig} from './version.config'
import {
    mapVersionCompany,
    mapVersionDevice,
    VersionCompaniesColumns,
    VersionDevicesColumns,
} from './version.constants'
import VersionRelatedList from './VersionRelatedList'
import '../../forms/AddNewDeviceForm/AddNewDeviceForm.scss'
import './VersionDetail.scss'

const formatFileSize = size => {
    if (!size && size !== 0) return '-'
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`

    return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const normalizeUrl = value => {
    if (!value) return null

    const markdownMatch = String(value).match(/\]\((https?:\/\/.+)\)$/)
    return markdownMatch ? markdownMatch[1] : value
}

const renderValue = value => {
    if (value === true) return 'true'
    if (value === false) return 'false'
    return value ?? '-'
}

const renderStorageMode = (isSaveRemotely, t) => {
    const Icon = isSaveRemotely ? Cloud : Server

    return (
        <Tag
            bordered
            color={isSaveRemotely ? 'green' : 'red'}
            className='version-detail__storage-tag'
        >
            <Icon size={14}/>
            <span>
                {isSaveRemotely
                    ? t('pages.versions.detail.storage.remote')
                    : t('pages.versions.detail.storage.local')}
            </span>
        </Tag>
    )
}

const downloadFile = async ({url, fileName}) => {
    const normalizedUrl = normalizeUrl(url)
    if (!normalizedUrl) return

    try {
        const response = await fetch(normalizedUrl)
        const blob = await response.blob()
        const downloadUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = fileName || 'version-file'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(downloadUrl)
    } catch (error) {
        const link = document.createElement('a')
        link.href = normalizedUrl
        link.download = fileName || 'version-file'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }
}

const FileInfo = ({title, name, size, contentType, url, t}) => {
    const normalizedUrl = normalizeUrl(url)

    return (
        <section className='create-device__forms-container version-detail__file-section'>
            <div className='create-device__section-header'>
                <FileArchive size={18}/>
                <h2 className='create-device__forms-title'>{title}</h2>
            </div>
            <Descriptions bordered size='small' column={1}>
                <Descriptions.Item label={t('pages.versions.detail.fields.fileName')}>
                    {name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('pages.versions.detail.fields.fileSize')}>
                    {formatFileSize(size)}
                </Descriptions.Item>
                <Descriptions.Item label={t('pages.versions.detail.fields.contentType')}>
                    {contentType || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('pages.versions.detail.fields.download')}>
                    {normalizedUrl ? (
                        <Button
                            icon={<Download size={14}/>}
                            onClick={() => downloadFile({url: normalizedUrl, fileName: name})}
                        >
                            {t('pages.versions.detail.download')}
                        </Button>
                    ) : (
                        '-'
                    )}
                </Descriptions.Item>
            </Descriptions>
        </section>
    )
}

const VersionDetail = ({config = normalVersionConfig}) => {
    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const [userData, setUserData] = useState({})
    const [versionData, setVersionData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const isStaff = Boolean(userData?.data?.is_admin)
    const canChangeVersion = isStaff || resolveIsEditPermission(userData?.data)
    const isSaveRemotely = versionData?.is_save_remotely === true

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'))
        if (items) {
            setUserData(items)
        }
    }, [])

    useEffect(() => {
        if (!userData.token || !id) return

        const fetchVersionDetail = async () => {
            setIsLoading(true)
            try {
                const response = await APIv1.get(config.endpoints.detail(id), {
                    headers: getAuthHeaders(userData.token),
                })
                setVersionData(response.data)
            } catch (error) {
                console.error('Error fetching version detail:', error)
                message.error(t('pages.versions.detail.messages.fetchError'))
            } finally {
                setIsLoading(false)
            }
        }

        fetchVersionDetail()
    }, [userData.token, id, t, config])

    const renderStatus = status => {
        const statusTag = versionStatusTag(status) || {}

        return (
            <Tag bordered={false} color={statusTag.color || 'default'}>
                {statusTag.icon} {status || '-'}
            </Tag>
        )
    }

    if (isLoading) {
        return (
            <div className='content_container create-device version-detail version-detail__loader'>
                <Spin/>
            </div>
        )
    }

    return (
        <div className='content_container create-device version-detail'>
            <div className='create-device__header'>
                <div className='create-device__heading'>
					<span className='create-device__eyebrow'>
						{t('pages.versions.detail.eyebrow')}
					</span>
                    <h1>
                        {t(config.detailTitleKey)} {versionData?.version_number}
                    </h1>
                    {versionData?.id && (
                        <span className='create-device__serial'>#{versionData.id}</span>
                    )}
                </div>
                <div className='version-detail__actions'>
                    {isStaff && (
                        <Button
                            className='create-device__action-button create-device__action-button--secondary'
                            icon={<Pencil size={16}/>}
                            type='primary'
                            onClick={() => navigate(config.routes.edit(id))}
                        >
                            {t('common.edit')}
                        </Button>
                    )}
                    <Button
                        className='create-device__action-button create-device__action-button--secondary'
                        icon={<ArrowLeft size={16}/>}
                        onClick={() => navigate(-1)}
                    >
                        {t('common.backBtn')}
                    </Button>
                </div>
            </div>

            <div className='create-device__forms'>
                <section className='create-device__forms-container'>
                    <div className='create-device__section-header'>
                        <span className='create-device__section-index'>01</span>
                        <h2 className='create-device__forms-title'>
                            {t('pages.versions.detail.infoSectionTitle')}
                        </h2>
                    </div>
                    <Descriptions bordered size='small' column={2}>
                        <Descriptions.Item label={t('common.id')}>
                            {renderValue(versionData?.id)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('pages.versions.create.fields.status')}>
                            {renderStatus(versionData?.status)}
                        </Descriptions.Item>
                        <Descriptions.Item
                            label={t('pages.versions.create.fields.versionNumber')}
                        >
                            {renderValue(versionData?.version_number)}
                        </Descriptions.Item>
                        <Descriptions.Item
                            label={t('pages.versions.create.fields.versionIndex')}
                        >
                            {renderValue(versionData?.version_number_index)}
                        </Descriptions.Item>
                        <Descriptions.Item
                            label={t('pages.versions.create.fields.isSaveRemotely')}
                        >
                            {renderStorageMode(isSaveRemotely, t)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('pages.versions.create.fields.comment')}>
                            {renderValue(versionData?.comment)}
                        </Descriptions.Item>
                    </Descriptions>
                </section>

                <FileInfo
                    title={t(config.fileFieldKey)}
                    name={versionData?.file_name}
                    size={versionData?.file_size}
                    contentType={versionData?.file_content_type}
                    url={versionData?.file_url || versionData?.file}
                    t={t}
                />

                {config.endpoints.devices && config.endpoints.companies && (
                    <div className='version-detail__related'>
                        <VersionRelatedList
                            index='02'
                            title={t('pages.versions.detail.related.devicesTitle')}
                            endpoint={config.endpoints.devices(id)}
                            columns={VersionDevicesColumns(t)}
                            mapItem={mapVersionDevice}
                            searchPlaceholder={t('pages.versions.detail.related.searchDevices')}
                            canEdit={canChangeVersion}
                            updateEndpoint={config.endpoints.updateDevices}
                            versionsEndpoint={config.endpoints.list}
                            currentVersionId={id}
                            confirmKey='pages.versions.detail.related.change.confirmDevices'
                            fastConfirmKey='pages.versions.detail.related.change.confirmFastDevices'
                            token={userData.token}
                        />
                        <VersionRelatedList
                            index='03'
                            title={t('pages.versions.detail.related.companiesTitle')}
                            endpoint={config.endpoints.companies(id)}
                            columns={VersionCompaniesColumns(t)}
                            mapItem={mapVersionCompany}
                            searchPlaceholder={t('pages.versions.detail.related.searchCompanies')}
                            canEdit={canChangeVersion}
                            updateEndpoint={config.endpoints.updateCompanies}
                            versionsEndpoint={config.endpoints.list}
                            currentVersionId={id}
                            confirmKey='pages.versions.detail.related.change.confirmCompanies'
                            fastConfirmKey='pages.versions.detail.related.change.confirmFastCompanies'
                            token={userData.token}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default VersionDetail
